import { getAnalyticsContext, getContextProviders } from './context';
import { isSnowplowSuppressed } from './suppression';
import {
  type AnalyticsDestination,
  type DestinationPayload,
  type TrackEvent,
} from './types';

/**
 * Common enrichment + per-destination shaping — the SINGLE source (containment
 * spec / D11). This reproduces the legacy `trackEventV2` effect's observable
 * output exactly; the committed parity snapshot
 * (`./parity/expected.ts`, captured from the effect BEFORE extraction) is the
 * contract. Do not change spread order or precedence without updating and
 * reviewing the snapshot — Datadog monitors and BI dashboards ride on it.
 *
 * Context providers are read at call time: an event tracked before app wiring
 * registers them (`src/lib/analytics/wiring.ts`) enriches from an empty
 * registry and is buffered with those impoverished props. No production path
 * does this today — module evaluation completes before any track() fires —
 * but keep it in mind for non-app entry points (tests, Storybook).
 */

/** `hardware_*` / `native_firmware_*` / catalogue `device_event` carry the Chronicle device snapshot. */
const CHRONICLE_EVENT_PREFIXES = ['hardware_', 'native_firmware_'] as const;

function isChronicleDeviceEvent(eventName: string): boolean {
  return (
    eventName === 'device_event' ||
    CHRONICLE_EVENT_PREFIXES.some((prefix) => eventName.startsWith(prefix))
  );
}

function getScreenDimensions(): Record<string, number> | Record<string, never> {
  if (typeof window === 'undefined') return {};
  return {
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    device_pixel_ratio: window.devicePixelRatio,
  };
}

export type EnrichedEvent = {
  route: ReadonlySet<AnalyticsDestination>;
  payloads: ReadonlyMap<AnalyticsDestination, DestinationPayload>;
};

export function enrich(trackingEvent: TrackEvent): EnrichedEvent {
  const { event_name, props, amplitudeProperties, pageUrl, options } = trackingEvent;
  const providers = getContextProviders();
  const { teamId } = getAnalyticsContext();
  const sessionLayoutVersion = providers.getSessionLayoutVersion?.();

  // EVI-1486: user-local timezone alongside event timestamps. Some embedded
  // WebViews don't fully implement Intl — fail silent, the event still ships.
  let userTimezone: string | undefined;
  let userUtcOffsetMinutes: number | undefined;
  try {
    userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    userUtcOffsetMinutes = -new Date().getTimezoneOffset();
  } catch {
    // best-effort
  }

  const extendedProps: Record<string, unknown> = {
    // System identity bag (device/app/platform/version/os/browser) — spread
    // FIRST, so caller props may override (e.g. `device_type`).
    ...(providers.getAppTraits?.() ?? {}),
    ...(userTimezone ? { timezone: userTimezone } : {}),
    ...(userUtcOffsetMinutes !== undefined
      ? { utc_offset_minutes: userUtcOffsetMinutes }
      : {}),
    ...getScreenDimensions(),
    ...(props ?? {}),
    // Placed AFTER the props spread so the system value always wins over any
    // accidental `installer_type` / deployment / install-source key in caller props.
    installer_type: providers.getInstallerType?.(),
    ...(providers.getDeploymentProps?.() ?? {}),
    ...(providers.getInstallSourceProps?.() ?? {}),
  };

  // The committed layout is system context, not caller input. Remove any
  // caller-supplied value first so stale/manual props cannot label events after
  // the session layout has unmounted; Snowplow still receives raw props below.
  delete extendedProps.layout_version;
  if (sessionLayoutVersion) {
    extendedProps.layout_version = sessionLayoutVersion;
  }

  // APP-6285: Chronicle (Heidi Remote) device-state snapshot — fill only keys
  // the caller didn't set, so event-specific values win.
  // Snapshot once so Snowplow (below) sends identical values.
  const chronicleDeviceProps = isChronicleDeviceEvent(event_name)
    ? (providers.getChronicleDeviceProps?.() ?? {})
    : undefined;
  if (chronicleDeviceProps) {
    for (const [key, value] of Object.entries(chronicleDeviceProps)) {
      if (!(key in extendedProps)) {
        extendedProps[key] = value;
      }
    }
  }

  if ('sessionId' in extendedProps) {
    extendedProps['session_id'] = extendedProps['sessionId'];
    delete extendedProps['sessionId'];
  }
  if (!('session_id' in extendedProps)) {
    const sessionId = providers.getSessionId?.();
    if (sessionId) {
      extendedProps['session_id'] = sessionId;
    }
  }

  const isAmplitudeEnabled = options?.amplitude !== false;
  const isPosthogEnabled = Boolean(options?.posthog);
  const isSegmentEnabled = Boolean(options?.segment);
  // Snowplow is on by default. Only bounded screen_viewed call sites may use
  // the explicit override; error_viewed remains unconditionally suppressed
  // because its free-text payload can carry PHI/PII.
  const isReviewedSnowplowOverride =
    options?.snowplow === true && event_name === 'screen_viewed';
  const isSnowplowEnabled =
    options?.snowplow !== false &&
    (!isSnowplowSuppressed(event_name) || isReviewedSnowplowOverride);

  const posthogProps = pageUrl
    ? { ...extendedProps, $current_url: pageUrl, attributed_url: pageUrl }
    : extendedProps;

  // Some approved product analytics fields are intended for Amplitude but
  // must not be copied into the shared telemetry bag. Keep this separate from
  // `extendedProps`: Datadog, Snowplow, PostHog, and Segment keep receiving
  // exactly their normal payload shapes.
  const amplitudeProps = amplitudeProperties
    ? { ...extendedProps, ...amplitudeProperties }
    : extendedProps;

  // Preserve a caller-provided `source` (e.g. Evidence chat's 'evidence') so
  // Datadog RUM `@context.source` reflects the real event origin; fall back to
  // 'rum_action' only when the event sets none. Previously this unconditionally
  // clobbered every event's source, collapsing all web/desktop RUM actions into
  // one 'rum_action' bucket and hiding per-source (e.g. Evidence) latency.
  // Only a non-empty string is a real source — null/undefined/non-string/empty
  // all default, so a stray value never becomes a useless `@context.source`.
  const callerSource = extendedProps.source;
  const datadogSource =
    typeof callerSource === 'string' && callerSource !== '' ? callerSource : 'rum_action';

  const datadogProps = {
    ...extendedProps,
    teamId,
    timestamp: new Date().toISOString(),
    source: datadogSource,
    ...(pageUrl && { attributed_url: pageUrl }),
  };

  const segmentProps = {
    ...extendedProps,
    ...(teamId && { $groups: { segment_group: teamId } }),
  };

  // Snowplow deliberately receives the RAW caller props — no common
  // enrichment, no `sessionId` normalization, and no `?? {}` default
  // (captured legacy behavior): a props-less event must reach the handler
  // layer as `undefined`. The generated spec functions throw on `undefined`
  // and `trackSnowplowEvent` catches + drops, so defaulting to `{}` would
  // emit events the legacy pipeline never sent (ARCFE-688; parity fixture
  // `no-props-event`).
  //
  // ONE deliberate exception (APP-8499): the Chronicle device snapshot IS
  // merged for `hardware_*` / `native_firmware_*` events — every hardware_*
  // Iglu schema declares the device fields, and without this merge the
  // enrichment never reached Snowplow at all. Caller props win, mirroring
  // the merge above. `NA` placeholders are stripped: the Iglu schemas type
  // battery/rssi as `integer | null`, so the string sentinel would fail
  // validation there (the schema-less destinations keep it).
  const snowplowProps = chronicleDeviceProps
    ? {
        ...Object.fromEntries(
          Object.entries(chronicleDeviceProps).filter(([, value]) => value !== 'NA')
        ),
        ...(props ?? {}),
      }
    : props;

  const route = new Set<AnalyticsDestination>(['datadog']);
  if (isSnowplowEnabled) route.add('snowplow');
  if (isAmplitudeEnabled) route.add('amplitude');
  if (isPosthogEnabled) route.add('posthog');
  if (isSegmentEnabled) route.add('segment');

  const payloads = new Map<AnalyticsDestination, DestinationPayload>([
    ['amplitude', { event: event_name, props: amplitudeProps }],
    ['posthog', { event: event_name, props: posthogProps }],
    [
      'segment',
      {
        event: event_name,
        props: segmentProps,
        extra: pageUrl ? { context: { page: { url: pageUrl } } } : undefined,
      },
    ],
    ['datadog', { event: event_name, props: datadogProps }],
    ['snowplow', { event: event_name, props: snowplowProps }],
  ]);

  return { route, payloads };
}
